import React from 'react';
import { Progress } from 'antd';
import { getChartColor } from 'helpers/calculations';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import './ScorePotentialGuage.scss';
import { withConfig } from 'hoc/config.hoc.js';
import { isObj } from 'helpers/objectHelpers';
import Label from 'components/form/Label/Label';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import { ReactComponent as ArrowUp } from 'style/_assets/icons/arrow-up.svg';
import { CreditScoreRangeActionLink } from 'components/Score/DashboardScore';
import { getActionName } from 'partial/OfferManager/module.constants';
import { Link } from 'components/navigation/Link/Link';
import classnames from 'classnames';

function InnerText({ innerText, datatestid, size }) {
  return (
    <React.Fragment>
      <div
        data-testid={datatestid ? `${datatestid}-innerText` : null}
        className={`${classnames('InnerText', {
          'text-20': size === 'medium',
          'text-14': size !== 'medium',
        })} `}
      >
        {innerText}
      </div>
    </React.Fragment>
  );
}

function TooltipUnwrapped({
  config,
  type,
  datatestid,
  pagePosition,
  hideText,
  vertical,
  tooltipContentClassName,
}) {
  const tooltipClassname = classnames({ margT0: type === 'SESAME_POTENTIAL' });

  if (!isObj(config)) {
    return '';
  }

  let scoreOverTime_oneMonth = {};
  let scoreOverTime_sixMonth = {};

  if (config) {
    scoreOverTime_oneMonth = {
      text: config.actions.new_card_score_increase_over_time.month1.text,
    };
    scoreOverTime_sixMonth = {
      text: config.actions.new_card_score_increase_over_time.month6.text,
    };
  }

  const configs = {
    SelfLender: {
      text: config ? config.actions.self_lender.text : '',
    },
    NewCardScoreIncrease: {
      text: config
        ? config.actions.new_card_score_increase.potential_increase
        : '',
    },
    NewCardScoreDecrease: {
      text: config
        ? config.actions.new_card_score_increase.potential_decrease
        : '',
    },
    ScoreOverTime_oneMonth: scoreOverTime_oneMonth,
    ScoreOverTime_sixMonth: scoreOverTime_sixMonth,
    CreditRepair_oneMonth: {
      text: config ? config.actions.credit_repair_expert.month1.text : '',
    },
    CreditRepair_sixMonth: {
      text: config ? config.actions.credit_repair_expert.month6.text : '',
    },
    SESAME_POTENTIAL: {
      text: 'Potential',
    },
    OcfScoreIncrease: {
      text: config ? config.ocf.increase_credit_limit.potential_increase : '',
    },
  };
  datatestid = datatestid ? `${datatestid}-Tooltip` : 'null';

  let tooltipText =
    type === 'SESAME_POTENTIAL'
      ? config.simulations.tooltipDisclaimerText.sesamePotential
      : config.simulations.tooltipDisclaimerText.inHouse;

  return (
    <div className="Tooltip" data-testid={datatestid}>
      <div className="TitleText" data-testid={`${datatestid}-TitleText`}>
        {!hideText && configs[type] && configs[type].text}
        <CSTooltip
          title={tooltipText}
          style={{ float: 'left' }}
          datatestid={datatestid}
          tooltipName={configs[type].text}
          pagePosition={pagePosition}
          className={tooltipClassname}
          isInline
          vertical={vertical}
          tooltipContentClassName={tooltipContentClassName}
        />
      </div>
    </div>
  );
}

const Tooltip = withConfig(TooltipUnwrapped);

function GuageError() {
  return <div>Data Error: cannot render guage graph</div>;
}

const SesamePotentialView = ({
  datatestid,
  scoreChange,
  creditScore,
  pagePosition,
  vertical,
}) => {
  return (
    <FlexContainer className="flex-column">
      <Label
        type="text-12"
        datatestid={datatestid ? `${datatestid}-scorePotential` : null}
        className={'scoreChangeSection'}
      >
        {scoreChange > 0 && (
          <FlexContainer className="align-items-center justify-center">
            <ArrowUp className="sesamePotentialArrowUp" role="img" />
            <Label
              datatestid={`${datatestid}-scorePotential-change`}
              className="text-14"
              color="fontBlue100"
            >
              {scoreChange}
            </Label>
          </FlexContainer>
        )}
      </Label>
      <div className="padB8"></div>
      <Label
        className="text-36 bold fontGray500"
        datatestid={datatestid ? `${datatestid}-scorePotential-score` : null}
      >
        {creditScore === 4 ? '--' : creditScore + scoreChange}
      </Label>
      <FlexContainer className="flex-row items-align-center justify-center potentialTooltip">
        <Label
          className="labelOverline text-12"
          color="fontBlue300"
          datatestid={
            datatestid ? `${datatestid}-scorePotential-scoreDescription` : null
          }
        >
          POTENTIAL
        </Label>
        <Tooltip
          hideText
          type="SESAME_POTENTIAL"
          pagePosition={pagePosition}
          vertical={vertical}
        />
      </FlexContainer>
    </FlexContainer>
  );
};

function getScoreFill(type, scaledScore, scoreChange, scaledMaxScore) {
  if (scaledScore <= 0) {
    return 0;
  }
  if (type === 'Increase') {
    if (scoreChange === 0) {
      return (scaledScore / scaledMaxScore) * 100;
    }
    return (scaledScore / scaledMaxScore) * 100;
  } else {
    if (scaledScore + scoreChange === 300) {
      return 0;
    } else {
      return ((scaledScore - Math.abs(scoreChange)) / scaledMaxScore) * 100;
    }
  }
}

function getPotentialChangeFill(
  type,
  scaledScore,
  scoreChange,
  scaledMaxScore,
) {
  if (scaledScore <= 0) {
    if (type === 'Increase') {
      return ((scoreChange - 300) / scaledMaxScore) * 100;
    }
    return 0;
  }
  if (type === 'Increase') {
    if (scoreChange + scaledScore >= scaledMaxScore) {
      return 100;
    }
    return ((scoreChange + scaledScore) / scaledMaxScore) * 100;
  } else {
    return (scaledScore / scaledMaxScore) * 100;
  }
}

function ScorePotentialGuage({
  creditScore,
  scoreChange,
  size,
  type,
  data,
  limit,
  datatestid,
  pagePosition,
  sesamePotential,
  sesamePotentialConfig,
  onLinkClickCallback,
  isSOT,
  vertical,
  tooltipContentClassName,
  title = '',
}) {
  let actionName = null;
  let noPointerClass = 'no-pointer';
  const scaledMaxScore = 550;
  const scaledScore = creditScore - 300;
  let actionLink = window.location.pathname;
  const potentialType = scoreChange >= 0 ? 'Increase' : 'Decrease';
  let scoreDisplay = scoreChange;

  if (potentialType === 'Increase') {
    if (scaledScore + scoreChange > scaledMaxScore) {
      scoreChange = scaledMaxScore - scaledScore;
    }
    scoreDisplay = scoreChange > 0 ? `+${scoreChange}` : `${scoreChange}`;
  } else if (potentialType === 'Decrease') {
    if (scaledScore - Math.abs(scoreChange) < 0) {
      scoreChange = scaledScore * -1;
    }
    scoreDisplay = `${scoreChange}`;
  }

  const width = {
    large: 142,
    medium: 136,
    small: 72,
    ocfMedium: 124,
  };

  const strokeColor = getChartColor(creditScore, sesamePotential);

  let scoreChangeForPotential = scoreChange;
  if (isSOT) {
    if (scoreChangeForPotential > 0 && scoreChangeForPotential < 5) {
      scoreChangeForPotential = 5;
    }
  }

  const scoreFill = getScoreFill(
    potentialType,
    scaledScore,
    scoreChange,
    scaledMaxScore,
  );
  const potentialFill = getPotentialChangeFill(
    potentialType,
    scaledScore,
    scoreChangeForPotential,
    scaledMaxScore,
  );
  if (sesamePotentialConfig) {
    actionName = sesamePotentialConfig.actionName;
    if (
      sesamePotentialConfig.actionName &&
      typeof sesamePotentialConfig.actionName === 'string'
    ) {
      actionLink = `/actions/${actionName}#sesamePotential`;
      noPointerClass = null;
    }
  }
  return (
    <div
      className={classnames(
        'ScorePotentialGuage text-center',
        potentialType,
        strokeColor,
      )}
      data-testid={datatestid ? `${datatestid}-ScorePotentialGuage` : null}
    >
      {typeof scoreChange !== 'number' ? (
        <GuageError />
      ) : (
        <>
          <Link
            linkClassName={noPointerClass}
            to={actionLink}
            onClickCallback={onLinkClickCallback}
            location={getActionName(actionName)}
            ariaLabel="gauge"
          >
            <Progress
              percent={potentialFill}
              success={{ percent: scoreFill }}
              width={size ? width[size] : width['small']}
              type="dashboard"
              title={title}
              format={() =>
                sesamePotential ? (
                  <SesamePotentialView
                    scoreChange={scoreChange}
                    creditScore={creditScore}
                    datatestid={datatestid}
                    pagePosition={pagePosition}
                    onLinkClickCallback={onLinkClickCallback}
                    actionName={actionName}
                    actionLink={actionLink}
                  />
                ) : (
                  <>
                    <InnerText
                      innerText={scoreDisplay}
                      datatestid={datatestid ? datatestid : null}
                      size={size}
                    />
                  </>
                )
              }
            />
            {sesamePotential && (
              <div className="dashboardScore">
                <CreditScoreRangeActionLink
                  showSesamePotential={true}
                  datatestid={datatestid}
                  onLinkClickCallback={onLinkClickCallback}
                  actionName={actionName}
                  actionLink={actionLink}
                />
              </div>
            )}
            <div className="padB18 "></div>
          </Link>
        </>
      )}
      {typeof scoreChange !== 'number' || !type ? (
        <></>
      ) : (
        <Tooltip
          datatestid={datatestid}
          type={type}
          score={scoreChange}
          limit={limit}
          data={data}
          pagePosition={pagePosition}
          vertical={vertical}
          tooltipContentClassName={tooltipContentClassName}
        />
      )}
    </div>
  );
}

export default ScorePotentialGuage;
